import { Link } from "gatsby";
import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Box, Flex } from "rebass/styled-components";

import { layout } from "../../constants/constants";
import ButtonWrapper from "../../shared/components/button-wrapper/button-wrapper";
import SecondaryNav from "../../shared/components/secondary-nav/secondary-nav";
import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useColumnEnd from "../../shared/hooks/column-end/useColumnEnd";
import useComponentAlerts from "../../shared/hooks/component-alerts/useComponentAlerts";
import useComponentVisible from "../../shared/hooks/component-visible/useComponentVisible";
import useFeatureToggle from "../../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, IconPack, SecondaryNavContent, SubsiteContent } from "../../shared/models/app-data-model";
import "../shared/navigation-shared.scss";
import "./subsite-nav-desktop.scss";

export type SubsiteNavDesktopProps = {
  content: SubsiteContent;
  featureToggles: FeatureToggle[];
  iconPack?: IconPack[];
};

const SubsiteNavDesktop: React.FC<SubsiteNavDesktopProps> = ({ content, featureToggles, iconPack }: SubsiteNavDesktopProps) => {
  const uiStateContext = useContext(UiStateContext);
  const subsiteNavTitleSwappingEnabled = useFeatureToggle("subsite-nav-title-vs-subsite-title", featureToggles);
  const [selectedSecondaryNavItem, setSelectedSecondaryNavItem] = useState([] as SecondaryNavContent[]);
  const [navColumns, setNavColumns] = useColumnEnd(selectedSecondaryNavItem);
  const [overflow, setOverflow] = useState(false);
  const [subsiteOpen, setsubsiteOpen] = useState(false);
  const [selectedNavIndex, setSelectedNavIndex] = useState<number>();
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(true, "subsite-nav-item");
  const buttonExists =
    content?.subsite_nav?.find((x) => x.button)?.button?.text && content?.subsite_nav?.find((x) => x.button)?.button?.reference?.length;
  // todo: should probably use button?.reference?.length instead of !== []
  const primaryNav = content.subsite_nav?.find((el) => el.primary_nav)?.primary_nav;

  useComponentAlerts(content.alerts || [], "subsite");

  // This hook is responsible for updating our column-end hook whenever our content changes
  useEffect(() => {
    setNavColumns(selectedSecondaryNavItem);
  }, [selectedSecondaryNavItem, setNavColumns]);

  const toggleMegaMenu = useCallback(
    (index: number, isItemOpen: boolean, NavItemData: SecondaryNavContent[]): void => {
      uiStateContext.setAuxNavOpen?.(false);
      uiStateContext.setSubsiteMenuOpen?.(!isItemOpen);
      uiStateContext.setSubsiteMenuOpenIndex?.(index);
      setSelectedSecondaryNavItem(NavItemData);
      setSelectedNavIndex(index);
    },
    [uiStateContext.setAuxNavOpen, uiStateContext.setSubsiteMenuOpen, uiStateContext.setSubsiteMenuOpenIndex]
  );

  const checkOverflow = useCallback((): void => {
    const elCheck = typeof document !== "undefined" && document.querySelector(".subsite-nav-items");
    // measure if nav elements are overflowing container
    if (elCheck && elCheck?.clientHeight < elCheck?.scrollHeight) {
      // items are overflowed need to add fix class.
      setOverflow(true);
    }
  }, [setOverflow]);

  const handleResize = useCallback((): void => {
    setOverflow(false); // clear out overflow check on resize
    checkOverflow();
  }, [setOverflow, checkOverflow]);

  useEffect(() => {
    // check initial load if class needs to be added. Delayed to ensure nav loaded.
    const delayInitialOverflowCheck = setTimeout(() => {
      checkOverflow();
    }, 500);

    return () => clearTimeout(delayInitialOverflowCheck);
  }, [checkOverflow]);

  useEffect(() => {
    // check overflow when window size changes
    window?.addEventListener("resize", handleResize);
    return () => window?.removeEventListener("resize", handleResize);
  }, [handleResize]);

  //Opening of mega menu should collaps the subsite nav
  useEffect(() => {
    if (uiStateContext.megaMenuOpen) {
      uiStateContext.setSubsiteMenuOpen?.(false);
    }
  }, [uiStateContext.megaMenuOpen]);

  // usually if the subsite uses it's own subsite nav, we want to take that as the title
  // but if the subsite nav points to a different subsite nav, we want to use that as the title
  // so we'll use the nav title unless it has a `|` in it

  //using useComponentVisible hook
  useEffect(() => {
    if (!isComponentVisible) {
      uiStateContext?.setSubsiteMenuOpen?.(false);
      setIsComponentVisible(true);
    }
  }, [isComponentVisible]);

  const subsiteNavTitle = content?.subsite_nav?.find((x) => x.title)?.title;
  const subsiteTitle = content?.subsite_name;
  const useParentSubsite = !subsiteNavTitle?.includes("|");
  const title = useParentSubsite ? subsiteNavTitle : subsiteTitle;
  const link = useParentSubsite ? content?.parent_subsite?.[0]?.url_prefix || "#" : content?.url_prefix;

  const subsiteNav = (
    <>
      {Object.keys(content).length > 0 && content.subsite_nav?.length > 0 && (
        <Box
          className={`subsite-nav-desktop-container ${uiStateContext?.megaMenuOpen ? "mega-menu-open" : "mega-menu-closed"}`}
          width="100%"
        >
          <Flex
            className={`subsite-nav-desktop ${uiStateContext?.megaMenuOpen ? "" : "mega-menu-closed"}`}
            width={layout.pageWidth}
            px={layout.pagePaddingX}
            mx="auto"
          >
            <div className={`left-content ${buttonExists ? "button-present" : ""}`}>
              <div className="subsite-nav-title-wrapper">
                <Link
                  className="subsite-nav-title"
                  title={subsiteNavTitleSwappingEnabled ? title : subsiteTitle}
                  to={subsiteNavTitleSwappingEnabled ? link : content?.url_prefix}
                >
                  {subsiteNavTitleSwappingEnabled ? title : subsiteTitle}
                </Link>
              </div>
              <nav
                className={`subsite-nav-items  ${overflow ? "subsite-nav-items-condensed" : ""}`}
                data-testid={overflow ? "subsiteNavCondensed" : "subsiteNav"}
                id="subsite-nav-desktop"
              >
                {primaryNav?.map((item, index) => {
                  const navItemWithChildren = item.primary_nav_item?.reference?.find((el) => el.primary_nav);
                  const navItemWithLink = item.primary_nav_item?.reference?.find((el) => el.url || el.link);
                  const navItemWithRegionalLink = item.primary_nav_item?.reference?.find(
                    (el) => el.internal?.type === "sh_regional_link_component"
                  );
                  const navItemOpenClass = uiStateContext.subsiteMenuOpen && uiStateContext.subsiteMenuOpenIndex === index ? "open" : "";
                  const isItemOpen = uiStateContext.subsiteMenuOpen && uiStateContext.subsiteMenuOpenIndex === index;

                  if (navItemWithChildren) {
                    return (
                      <div className="subsitenav-item-container">
                        <button
                          className={`primary-nav-item unstyled-btn ${navItemOpenClass}`}
                          id={`${index}-subsite-nav-item`}
                          key={`prim-nav-item-${index}`}
                          tabIndex={0}
                          data-testid={`subsite-nav-item-${index}`}
                          onClick={() => {
                            toggleMegaMenu(index, isItemOpen, navItemWithChildren.primary_nav as SecondaryNavContent[]);
                          }}
                        >
                          {item.primary_nav_item.title}
                          <ShIcon
                            iconPack={iconPack}
                            iconName="caret_down"
                            className="subsite-nav-caret-down-icon"
                            elemId={`primary-nav-item-${index}-icon`}
                          />
                        </button>
                        <div
                          className={`mm-cyan-border-bottom ${
                            selectedNavIndex === index && uiStateContext.subsiteMenuOpen && "subsite-link-active"
                          }`}
                        ></div>
                      </div>
                    );
                  } else if (navItemWithLink || navItemWithRegionalLink) {
                    return (
                      <div className="subsitenav-item-container">
                        <ShLink
                          key={index}
                          className="primary-nav-item"
                          reference={item.primary_nav_item.reference}
                          title={item.primary_nav_item.title}
                          iconPack={iconPack}
                          featureToggles={[]}
                        />
                        <div
                          className={`mm-cyan-border-bottom ${
                            selectedNavIndex === index && uiStateContext.subsiteMenuOpen && "subsite-link-active"
                          }`}
                        ></div>
                      </div>
                    );
                  }
                })}
              </nav>
            </div>
            {buttonExists && (
              <div className="right-content">
                <span className="subsite-nav-button">
                  <ButtonWrapper
                    type="gradient"
                    label={content?.subsite_nav.find((x) => x.button)?.button?.text}
                    reference={content?.subsite_nav?.find((x) => x.button)?.button?.reference}
                    iconPack={iconPack}
                  />
                </span>
              </div>
            )}
          </Flex>
          {uiStateContext.subsiteMenuOpen && selectedSecondaryNavItem && isComponentVisible && (
            <div className="secondary-nav-wrapper" data-testid="subsite-secondary-nav" ref={ref}>
              <SecondaryNav columns={navColumns} isOutsideClickHandlerEnabled={true} iconPack={iconPack} />
            </div>
          )}
        </Box>
      )}
    </>
  );
  return subsiteNav;
};

export default SubsiteNavDesktop;
